// src/pages/Buyer/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ShoppingCart,
  LocalShipping,
  AttachMoney,
  VerifiedUser,
  Add,
  ArrowForward,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { enquiryAPI, verificationAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    pendingEnquiries: 0,
    activeShipments: 0,
    pendingPayments: 0,
  });
  
  const [enquiries, setEnquiries] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationLoading, setVerificationLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchVerificationStatus();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await enquiryAPI.getUserEnquiries();
      const userEnquiries = response.data;
      
      setEnquiries(userEnquiries.slice(0, 5)); // Show only 5 recent
      
      const stats = {
        totalEnquiries: userEnquiries.length,
        pendingEnquiries: userEnquiries.filter(e => e.status === 'pending').length,
        activeShipments: userEnquiries.filter(e => 
          ['confirmed', 'shipped', 'in_transit'].includes(e.status)
        ).length,
        pendingPayments: userEnquiries.filter(e => e.status === 'confirmed').length,
      };
      
      setStats(stats);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      setVerificationLoading(true);
      const response = await verificationAPI.getStatus();
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setVerificationLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Enquiries',
      value: stats.totalEnquiries,
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      description: 'All enquiries made',
    },
    {
      title: 'Pending Enquiries',
      value: stats.pendingEnquiries,
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      color: '#ff9800',
      description: 'Awaiting response',
    },
    {
      title: 'Active Shipments',
      value: stats.activeShipments,
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      color: '#2196f3',
      description: 'In transit',
    },
    {
      title: 'Pending Payments',
      value: stats.pendingPayments,
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      description: 'Awaiting payment',
    },
  ];

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.company_name || user?.email}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your fruit trading dashboard
        </Typography>
      </Box>

      {/* Verification Alert */}
      {!verificationLoading && verificationStatus && !verificationStatus.user_verified && (
        <Alert 
          severity="warning" 
          sx={{ mb: 4 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => navigate('/buyer/verification')}
              endIcon={<ArrowForward />}
            >
              Complete Now
            </Button>
          }
        >
          <Typography variant="body1" fontWeight="medium">
            Verification Required
          </Typography>
          <Typography variant="body2">
            Please complete your business verification to make enquiries and access all features.
          </Typography>
        </Alert>
      )}

      {verificationStatus?.user_verified && (
        <Alert 
          severity="success" 
          sx={{ mb: 4 }}
          icon={<CheckCircle />}
        >
          <Typography variant="body1" fontWeight="medium">
            Account Verified ✓
          </Typography>
          <Typography variant="body2">
            Your business verification is complete. You can now make enquiries and trade.
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: stat.color }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {stat.description}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/fruits')}
                fullWidth
                size="large"
              >
                Browse Fruits & Make Enquiry
              </Button>
              
              {!verificationStatus?.user_verified && (
                <Button
                  variant="outlined"
                  startIcon={<VerifiedUser />}
                  onClick={() => navigate('/buyer/verification')}
                  fullWidth
                  size="large"
                  color="warning"
                >
                  Complete Verification
                </Button>
              )}
              
              <Button
                variant="outlined"
                startIcon={<ShoppingCart />}
                onClick={() => navigate('/buyer/enquiries')}
                fullWidth
                size="large"
              >
                View All Enquiries
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<LocalShipping />}
                onClick={() => navigate('/buyer/shipments')}
                fullWidth
                size="large"
              >
                Track Shipments
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Account Status
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Email Verification</Typography>
                <Chip label="Verified" color="success" size="small" icon={<CheckCircle />} />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Business Verification</Typography>
                {verificationLoading ? (
                  <CircularProgress size={20} />
                ) : verificationStatus?.user_verified ? (
                  <Chip label="Verified" color="success" size="small" icon={<CheckCircle />} />
                ) : (
                  <Chip 
                    label="Pending" 
                    color="warning" 
                    size="small" 
                    icon={<Warning />}
                    onClick={() => navigate('/buyer/verification')}
                    clickable
                  />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Account Type</Typography>
                <Chip label={user?.user_type === 'admin' ? 'Administrator' : 'Buyer'} color="primary" size="small" />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Member Since</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user?.created_at)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Enquiries */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Recent Enquiries</Typography>
          <Button
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/buyer/enquiries')}
          >
            View All
          </Button>
        </Box>
        
        {enquiries.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You haven't made any enquiries yet. Start by browsing our fruits collection.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => navigate('/fruits')}
            >
              Browse Fruits
            </Button>
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Enquiry ID</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} hover>
                    <TableCell>#{enquiry.id}</TableCell>
                    <TableCell>Fruit #{enquiry.fruit_id}</TableCell>
                    <TableCell>{enquiry.quantity} containers</TableCell>
                    <TableCell>{formatDate(enquiry.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={enquiry.status}
                        color={getStatusColor(enquiry.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/buyer/enquiries/${enquiry.id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Next Steps */}
      {!verificationStatus?.user_verified && (
        <Paper sx={{ p: 3, bgcolor: 'warning.light', color: 'warning.dark' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning /> Next Steps Required
          </Typography>
          <Typography variant="body2" paragraph>
            To start trading on our platform, you need to complete the following:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>
              <Typography variant="body2">
                Complete business verification with required documents
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Browse fruits and make your first enquiry
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Complete payment verification for your first order
              </Typography>
            </li>
          </Box>
          <Button
            variant="contained"
            color="warning"
            onClick={() => navigate('/buyer/verification')}
            startIcon={<VerifiedUser />}
          >
            Start Verification
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default BuyerDashboard;